import { useState, useEffect } from 'react'
import { Button, Toast, Loading, Empty, Input, Tag, Dialog, NavBar } from 'react-vant'
import { ArrowLeft, Description, ArrowDown, DeleteO, StarO, Star, LikeO, Like } from '@react-vant/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import request from '../../api/request'

export default function PostDetail() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.user)
    const [post, setPost] = useState(null)
    const [comments, setComments] = useState([])
    const [loading, setLoading] = useState(true)
    const [commentText, setCommentText] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const handleDeletePost = () => {
        Dialog.confirm({
            title: '确认删除',
            message: '确定要删除这条动态吗？删除后不可恢复。',
            onConfirm: async () => {
                try {
                    await request.delete(`/hub/posts/${id}/`)
                    Toast.success('动态已删除')
                    navigate(-1)
                } catch (e) {
                    Toast.fail(e.message || '删除失败')
                }
            },
        })
    }

    const handleDeleteComment = (commentId) => {
        Dialog.confirm({
            title: '确认删除',
            message: '确定要删除这条评论吗？',
            onConfirm: async () => {
                try {
                    await request.delete(`/hub/comments/${commentId}/`)
                    setComments(prev => prev.filter(c => c.id !== commentId))
                    Toast.success('已删除')
                } catch (e) {
                    Toast.fail(e.message || '删除失败')
                }
            },
        })
    }

    const handleLike = async () => {
        try {
            const res = await request.post(`/hub/posts/${id}/like/`)
            setPost(prev => ({ ...prev, like_count: res.like_count }))
        } catch (e) {
            console.error(e)
        }
    }

    const handleTogglePin = async () => {
        try {
            const res = await request.post(`/hub/posts/${id}/pin/`)
            setPost(prev => ({ ...prev, is_pinned: res.is_pinned }))
            Toast.info(res.message)
        } catch (e) {
            Toast.fail(e.message || '操作失败')
        }
    }

    const fetchDetail = async () => {
        try {
            const [postRes, commentRes] = await Promise.all([
                request.get(`/hub/posts/${id}/`),
                request.get(`/hub/posts/${id}/comments/`)
            ])
            setPost(postRes)
            setComments(commentRes)
        } catch (e) {
            console.error(e)
            Toast.fail('获取详情失败')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchDetail()
    }, [id])

    const handleSendComment = async () => {
        if (!commentText.trim()) return
        setSubmitting(true)
        try {
            await request.post(`/hub/posts/${id}/comments/`, { content: commentText })
            setCommentText('')
            Toast.success('评论成功')
            fetchDetail()
        } catch (e) {
            Toast.fail('发送失败')
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}><Loading type="spinner" color="#056b4f" size="36px" /></div>
    if (!post) return <div style={{ minHeight: '100vh', background: '#f8fafc', paddingTop: 100 }}><Empty description="文章不存在" /></div>

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', paddingBottom: 100, fontFamily: '"Inter", "PingFang SC", "Microsoft YaHei", sans-serif' }}>
            {/* Header */}
            <NavBar
                title="动态详情"
                leftText={<ArrowLeft />}
                onClickLeft={() => navigate(-1)}
                rightText={
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        {userInfo?.role === 'admin' && (
                            <div onClick={handleTogglePin}>
                                {post?.is_pinned ? <Star color="#fdcb6e" /> : <StarO />}
                            </div>
                        )}
                        {(userInfo?.role === 'admin' || userInfo?.id === post?.author_id) && <DeleteO color="#e11d48" onClick={handleDeletePost} />}
                    </div>
                }
                fixed
                placeholder
                style={{ '--van-nav-bar-background': 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)' }}
            />

            <div style={{ padding: '24px 20px', background: '#fff', borderRadius: '0 0 24px 24px', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
                {/* 标题 */}
                <h1 style={{ margin: '0 0 16px 0', fontSize: 24, lineHeight: 1.4, fontWeight: 800, color: '#0f172a' }}>
                    {post.is_official && <Tag color="linear-gradient(135deg, #ff5e1e, #e11d48)" textStyle={{ fontWeight: 700 }} size="medium" style={{ marginRight: 8, verticalAlign: 'middle', borderRadius: 6 }}>官方</Tag>}
                    <span style={{ verticalAlign: 'middle' }}>{post.title}</span>
                </h1>

                {/* 作者信息 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #a7f3d0 0%, #6ee7b7 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700, boxShadow: '0 4px 10px rgba(52, 211, 153, 0.4)' }}>
                        {post.author_name?.charAt(0)}
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#334155' }}>{post.author_name}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{new Date(post.created_at).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} · {post.view_count} 次阅读</div>
                    </div>
                </div>

                {/* 正文 */}
                <div style={{ fontSize: 16, lineHeight: 1.8, color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {post.content}
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                    <Button 
                        icon={<LikeO color="#ff7675" />} 
                        round 
                        onClick={handleLike}
                        style={{ border: '1px solid #ff7675', color: '#ff7675', padding: '0 30px' }}
                    >
                        {post.like_count || 0}
                    </Button>
                </div>

                {/* 附件 */}
                {post.attachments?.length > 0 && (
                    <div style={{ marginTop: 32 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6, color: '#0f172a' }}>
                            <Description /> 附件资源 ({post.attachments.length})
                        </div>
                        {post.attachments.map(att => (
                            <div 
                                key={att.id} 
                                style={{ 
                                    padding: '16px', 
                                    background: '#f8fafc', 
                                    borderRadius: 16, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 16,
                                    marginBottom: 12,
                                    border: '1px solid #e2e8f0',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}
                            >
                                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(5, 107, 79, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Description size={24} color="#056b4f" />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 15, fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>{att.file_name}</div>
                                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{Math.round(att.file_size / 1024)} KB</div>
                                </div>
                                <div 
                                    onClick={() => window.open(att.file_url)}
                                    style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', cursor: 'pointer' }}
                                >
                                    <ArrowDown size={18} color="#056b4f" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 评论区 */}
            <div style={{ padding: '24px 20px' }}>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 20, color: '#0f172a' }}>全部评论 ({comments.length})</div>
                
                {comments.length === 0 ? (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                        <div style={{ fontSize: 40, marginBottom: 10 }}>💬</div>
                        成为第一个评论的人吧
                    </div>
                ) : (
                    comments.map(comment => (
                        <div key={comment.id} style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 18, background: 'linear-gradient(135deg, #fdfbfb 0%, #e2e8f0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#64748b', border: '1px solid #e2e2e2' }}>
                                {comment.author_name?.charAt(0)}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                    <span style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>{comment.author_name}</span>
                                    <span style={{ fontSize: 11, color: '#cbd5e1' }}>{new Date(comment.created_at).toLocaleString('zh-CN', { month: 'short', day: 'numeric' })}</span>
                                </div>
                                <div style={{ fontSize: 15, color: '#334155', lineHeight: 1.6, background: '#fff', padding: '12px 16px', borderRadius: '0 16px 16px 16px', display: 'inline-block', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                                    {comment.content}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Bottom Input Bar */}
            <div style={{ 
                position: 'fixed', bottom: 0, left: 0, right: 0, 
                background: 'rgba(255, 255, 255, 0.9)', 
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                padding: '12px 16px 20px', 
                borderTop: '1px solid rgba(0,0,0,0.05)', 
                display: 'flex', alignItems: 'center', gap: 12 
            }}>
                <div style={{ flex: 1, position: 'relative' }}>
                    <Input
                        value={commentText}
                        onChange={setCommentText}
                        placeholder="想说点什么..."
                        style={{ background: '#f1f5f9', borderRadius: 24, padding: '10px 20px', fontSize: 15 }}
                    />
                </div>
                <Button 
                    color="linear-gradient(135deg, #055e43, #0c8c68)"
                    round 
                    loading={submitting}
                    disabled={!commentText.trim()}
                    onClick={handleSendComment}
                    style={{ width: 44, height: 44, padding: 0, border: 'none', boxShadow: commentText.trim() ? '0 4px 12px rgba(5, 107, 79, 0.3)' : 'none', opacity: commentText.trim() ? 1 : 0.5 }}
                >
                    发送
                </Button>
            </div>
        </div>
    )
}
